import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Eye,
  Loader2,
  ChevronLeft,
  ChevronRight,
  History,
  PlayCircle,
  Filter,
} from 'lucide-react';
import { useGetClinicalSessionsPaged } from '../hooks/ClinicalSession';
import {
  BaseClinicalSession,
  ClinicalSessionStatus,
  ClinicalSessionStep,
} from '../dtos/ClinicalSessions/index';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

// Status display configuration
const STATUS_CONFIG: Record<ClinicalSessionStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; className: string }> = {
  [ClinicalSessionStatus.Draft]: { label: 'Draft', variant: 'secondary', className: 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100' },
  [ClinicalSessionStatus.InProgress]: { label: 'În Progres', variant: 'default', className: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100' },
  [ClinicalSessionStatus.Completed]: { label: 'Finalizat', variant: 'outline', className: 'bg-green-50 text-green-700 border-green-300 hover:bg-green-100' },
  [ClinicalSessionStatus.Abandoned]: { label: 'Abandonat', variant: 'destructive', className: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100' },
};

// Step display configuration
const STEP_LABELS: Record<ClinicalSessionStep, string> = {
  [ClinicalSessionStep.Person]: 'Date Personale',
  [ClinicalSessionStep.Assessment]: 'Evaluare',
  [ClinicalSessionStep.Results]: 'Rezultate',
  [ClinicalSessionStep.Guidance]: 'Sumar',
  [ClinicalSessionStep.Completed]: 'Finalizat',
};

function getStatusBadge(status?: ClinicalSessionStatus) {
  if (status === undefined || status === null) {
    return <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">Necunoscut</Badge>;
  }
  const config = STATUS_CONFIG[status];
  return (
    <Badge variant={config?.variant || 'outline'} className={config?.className || ''}>
      {config?.label || 'Necunoscut'}
    </Badge>
  );
}

function getStepLabel(step?: ClinicalSessionStep): string {
  if (step === undefined || step === null) return 'Necunoscut';
  return STEP_LABELS[step] || 'Necunoscut';
}

function formatDate(dateString?: string): string {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString('ro-RO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
}

function getPersonDisplayName(session: BaseClinicalSession): string {
  if (session.person) {
    const parts = [];
    if (session.person.firstName) parts.push(session.person.firstName);
    if (session.person.lastName) parts.push(session.person.lastName);
    if (parts.length > 0) return parts.join(' ');
  }
  if (session.personInternalId) return `ID: ${session.personInternalId}`;
  return '-';
}

function getLatestRiskLevel(session: BaseClinicalSession): string {
  const riskCalculations = session.riskCalculations;
  if (!riskCalculations || riskCalculations.length === 0) {
    return '-';
  }

  const latest = riskCalculations.reduce((currentLatest, candidate) => {
    if (!currentLatest) return candidate;
    const currentDate = currentLatest.calculatedAt ? Date.parse(currentLatest.calculatedAt) : 0;
    const candidateDate = candidate.calculatedAt ? Date.parse(candidate.calculatedAt) : 0;
    return candidateDate >= currentDate ? candidate : currentLatest;
  }, riskCalculations[0]);

  return latest?.result?.riskLevel || '-';
}

function getRiskLevelBadge(riskLevel: string) {
  const normalized = riskLevel.toLowerCase();
  const config: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; className: string }> = {
    low: { label: 'Low', variant: 'secondary', className: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100' },
    medium: { label: 'Medium', variant: 'default', className: 'bg-yellow-50 text-yellow-800 border-yellow-200 hover:bg-yellow-100' },
    high: { label: 'High', variant: 'destructive', className: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100' },
    veryhigh: { label: 'Very High', variant: 'destructive', className: 'bg-red-100 text-red-800 border-red-300 hover:bg-red-200' },
  };

  const display = config[normalized];
  if (!display) {
    return <span>-</span>;
  }

  return (
    <Badge variant={display.variant} className={display.className}>
      {display.label}
    </Badge>
  );
}

export default function ClinicalSessions() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get URL params
  const page = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(searchParams.get('size') || '10', 10);
  const statusParam = searchParams.get('status');
  
  // Parse status - null means "All"
  const selectedStatus: ClinicalSessionStatus | null = statusParam !== null && statusParam !== 'all' 
    ? parseInt(statusParam, 10) as ClinicalSessionStatus 
    : null;

  // Fetch data
  const { data: response, isLoading, isFetching } = useGetClinicalSessionsPaged(page, pageSize, selectedStatus);

  const pagedData = response?.isSuccess ? response.data : null;
  const sessions = pagedData?.items || [];
  const totalCount = pagedData?.totalCount || 0;
  const totalPages = pagedData?.totalPages || 1;

  // Update URL params
  const updateParams = (updates: { page?: number; size?: number; status?: string | null }) => {
    const newParams = new URLSearchParams(searchParams);
    
    if (updates.page !== undefined) {
      if (updates.page === 1) {
        newParams.delete('page');
      } else {
        newParams.set('page', updates.page.toString());
      }
    }
    
    if (updates.size !== undefined) {
      if (updates.size === 10) {
        newParams.delete('size');
      } else {
        newParams.set('size', updates.size.toString());
      }
    }
    
    if (updates.status !== undefined) {
      if (updates.status === null || updates.status === 'all') {
        newParams.delete('status');
      } else {
        newParams.set('status', updates.status);
      }
    }
    
    setSearchParams(newParams);
  };

  const handleStatusChange = (value: string) => {
    updateParams({ status: value === 'all' ? null : value, page: 1 });
  };

  const handlePageSizeChange = (value: string) => {
    updateParams({ size: parseInt(value, 10), page: 1 });
  };

  const handlePageChange = (newPage: number) => {
    updateParams({ page: newPage });
  };

  const handleViewSession = (session: BaseClinicalSession) => {
    // Navigate to flow clinical with session ID to resume
    navigate(`/flow-clinical?sessionId=${session.id}`);
  };

  const handleNewSession = () => {
    navigate('/flow-clinical');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <History className="h-8 w-8" />
            Sesiuni Clinice - Istoric
          </h1>
          <p className="text-muted-foreground mt-2">
            Vizualizați și gestionați sesiunile clinice anterioare
          </p>
        </div>
        <Button onClick={handleNewSession}>
          <PlayCircle className="h-4 w-4 mr-2" />
          Sesiune Nouă
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtre
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="w-[200px]">
              <Select
                value={selectedStatus !== null ? selectedStatus.toString() : 'all'}
                onValueChange={handleStatusChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Toate statusurile" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate</SelectItem>
                  <SelectItem value={ClinicalSessionStatus.Draft.toString()}>Draft</SelectItem>
                  <SelectItem value={ClinicalSessionStatus.InProgress.toString()}>În Progres</SelectItem>
                  <SelectItem value={ClinicalSessionStatus.Completed.toString()}>Finalizat</SelectItem>
                  <SelectItem value={ClinicalSessionStatus.Abandoned.toString()}>Abandonat</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-[120px]">
              <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <SelectItem key={size} value={size.toString()}>
                      {size} pe pagină
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="text-sm text-muted-foreground">
              {totalCount} {totalCount === 1 ? 'sesiune' : 'sesiuni'} găsite
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <History className="h-12 w-12 mb-4 opacity-50" />
              <p>Nu există sesiuni clinice</p>
              <Button variant="outline" className="mt-4" onClick={handleNewSession}>
                <PlayCircle className="h-4 w-4 mr-2" />
                Începe o sesiune nouă
              </Button>
            </div>
          ) : (
            <div className="relative">
              {isFetching && (
                <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              )}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Persoană</TableHead>
                    <TableHead>ID Intern</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Pas Curent</TableHead>
                    <TableHead>Progres</TableHead>
                    <TableHead>GDPR</TableHead>
                    <TableHead>Risc</TableHead>
                    <TableHead className="w-[100px]">Acțiuni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map((session) => (
                    <TableRow key={session.id}>
                      <TableCell className="font-medium">
                        {getPersonDisplayName(session)}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {session.personInternalId || '-'}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(session.progress?.status)}
                      </TableCell>
                      <TableCell>
                        {getStepLabel(session.progress?.currentStep)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 w-[120px]">
                          <Progress value={session.progress?.percentage || 0} className="h-2" />
                          <span className="text-xs text-muted-foreground w-8">
                            {session.progress?.percentage || 0}%
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={session.haveGdpr ? 'default' : 'destructive'}>
                          {session.haveGdpr ? 'Da' : 'Nu'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {getRiskLevelBadge(getLatestRiskLevel(session))}
                      </TableCell>
                      <TableCell>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleViewSession(session)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Vizualizare / Continuare</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {sessions.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Afișare {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, totalCount)} din {totalCount}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Pagina {page} din {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(page - 1)}
              disabled={page <= 1 || isFetching}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages || isFetching}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
